import { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { colorTextError } from "src/design/tokens";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { useInviteStaff } from "src/data/organization/queries/staff";

interface InviteStaffDialogProps {
  open: boolean;
  organizationId: string;
  onClose: () => void;
  onInvited: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const InviteStaffDialog = ({
  open,
  organizationId,
  onClose,
  onInvited,
}: InviteStaffDialogProps) => {
  const { t } = useTranslation();
  const { restaurants } = useContext(RestaurantContext);

  const [email, setEmail] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const inviteMutation = useInviteStaff(() => {
    onInvited();
    onClose();
  });

  useEffect(() => {
    if (open) {
      setEmail("");
      setRestaurantId("");
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!EMAIL_REGEX.test(email.trim())) {
      setError(t("myOrganization.staff.inviteEmailInvalid"));
      return;
    }
    if (!restaurantId) {
      setError(t("myOrganization.staff.inviteSelectRestaurant"));
      return;
    }

    setError(null);
    inviteMutation.mutate(
      { email: email.trim(), organizationId, restaurantId },
      {
        onError: (err) =>
          setError(
            err instanceof Error
              ? err.message
              : t("myOrganization.staff.inviteError")
          ),
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !inviteMutation.isPending && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t("myOrganization.staff.inviteTitle")}
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("myOrganization.staff.inviteSubtitle")}
        </Typography>

        <TextField
          label={t("myOrganization.staff.inviteEmail")}
          type="email"
          fullWidth
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth size="small">
          <InputLabel>{t("myOrganization.staff.inviteRestaurant")}</InputLabel>
          <Select
            value={restaurantId}
            label={t("myOrganization.staff.inviteRestaurant")}
            onChange={(e) => setRestaurantId(e.target.value)}
          >
            {restaurants.map((restaurant) => (
              <MenuItem key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Typography
            variant="caption"
            sx={{ color: colorTextError, mt: 1.5, display: "block" }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={inviteMutation.isPending}
        >
          {t("common.cancel")}
        </CustomButton>
        <CustomLoadingButton
          loading={inviteMutation.isPending}
          onSubmit={handleSubmit}
          label={t("myOrganization.staff.inviteSend")}
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
